import React, { useState, useEffect } from 'react';
import { X, ArrowRight, AlertCircle, Clock, RefreshCw } from 'lucide-react';
import { ApiService, Company, SwapQuote } from '../../../services/api';
import toast from 'react-hot-toast';

interface SwapModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  isExecuting: boolean;
  setIsExecuting: (isExecuting: boolean) => void;
}

const SwapModal: React.FC<SwapModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  isExecuting,
  setIsExecuting
}) => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [senderCompanyWallet, setSenderCompanyWallet] = useState('');
  const [receiverCompanyWallet, setReceiverCompanyWallet] = useState('');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState<SwapQuote | null>(null);
  const [isGeneratingQuote, setIsGeneratingQuote] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number>(0);

  useEffect(() => {
    if (isOpen) {
      fetchCompanies();
    } else {
      // Reset state when modal closes
      resetModal();
    }
  }, [isOpen]);

  // Countdown timer for quote expiry
  useEffect(() => {
    if (!quote || timeLeft <= 0) return;

    const timer = setInterval(() => {
      const now = new Date().getTime();
      const expiryTime = new Date(quote.expiresAt).getTime();
      const remaining = Math.max(0, Math.floor((expiryTime - now) / 1000));
      
      setTimeLeft(remaining);
      
      if (remaining === 0) {
        toast.error('Quote has expired. Please generate a new quote.');
        setQuote(null);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [quote, timeLeft]);

  const resetModal = () => {
    setSenderCompanyWallet('');
    setReceiverCompanyWallet('');
    setAmount('');
    setQuote(null);
    setTimeLeft(0);
  };

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const companiesData = await ApiService.getCompanies();
      // Filter only active companies with valid wallet addresses
      const activeCompanies = companiesData.filter(
        (c: Company) => c.status === 'ACTIVE' && c.companyWallet
      );
      setCompanies(activeCompanies);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const handleGetQuote = async () => {
    if (!senderCompanyWallet || !receiverCompanyWallet) {
      toast.error('Please select both sender and receiver companies');
      return;
    }

    if (senderCompanyWallet === receiverCompanyWallet) {
      toast.error('Sender and receiver must be different companies');
      return;
    }

    setIsGeneratingQuote(true);

    try {
      const newQuote = await ApiService.generateSwapQuote({
        senderCompanyWallet,
        receiverCompanyWallet,
      });

      setQuote(newQuote);
      
      // Calculate initial time left
      const now = new Date().getTime();
      const expiryTime = new Date(newQuote.expiresAt).getTime();
      const remaining = Math.max(0, Math.floor((expiryTime - now) / 1000));
      setTimeLeft(remaining);

      toast.success('Quote generated successfully!');
    } catch (error: any) {
      console.error('Failed to generate quote:', error);
      toast.error(error.response?.data?.message || 'Failed to generate quote');
    } finally {
      setIsGeneratingQuote(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!quote) {
      toast.error('Please generate a quote first');
      return;
    }

    if (!senderCompanyWallet || !receiverCompanyWallet || !amount) {
      toast.error('Please fill in all fields');
      return;
    }

    const amountInCents = Math.round(parseFloat(amount) * 100);
    if (amountInCents <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }

    if (amountInCents > quote.maxTransactionAmount) {
      toast.error(`Amount exceeds quote maximum of $${(quote.maxTransactionAmount / 100).toFixed(2)}`);
      return;
    }

    setIsExecuting(true);

    try {
      const result = await ApiService.executeSwap({
        senderCompanyWallet,
        receiverCompanyWallet,
        amount: amountInCents,
        quoteId: quote.quoteId,
      });

      toast.success(`Swap executed successfully! Transaction ID: ${result.transactionId}`);
      
      resetModal();
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Failed to execute swap:', error);
      const errorMessage = error.response?.data?.message || 'Failed to execute swap';
      
      if (errorMessage.includes('expired')) {
        setQuote(null);
        setTimeLeft(0);
      }
      
      toast.error(errorMessage);
    } finally {
      setIsExecuting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getSenderCompany = () => {
    return companies.find(c => c.companyWallet === senderCompanyWallet);
  };

  const getReceiverCompany = () => {
    return companies.find(c => c.companyWallet === receiverCompanyWallet);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#182233] rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white">Execute Swap</h2>
          <button
            onClick={onClose}
            disabled={isExecuting || isGeneratingQuote}
            className="text-gray-400 hover:text-white"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2777EF]"></div>
            </div>
          ) : (
            <>
              <div className="space-y-6">
                {/* Info Alert */}
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 flex items-start">
                  <AlertCircle className="h-5 w-5 text-blue-400 mr-3 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-200">
                    <p className="font-semibold mb-1">Quote-Based Swaps</p>
                    <p>
                      Generate a quote to get the exchange rate and maximum transaction amount. 
                      Quotes are valid for 1 minute. You must execute the swap before the quote expires.
                    </p>
                  </div>
                </div>

                {/* Sender Company */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Sender Company
                  </label>
                  <select
                    value={senderCompanyWallet}
                    onChange={(e) => {
                      setSenderCompanyWallet(e.target.value);
                      setQuote(null); // Reset quote when company changes
                      setTimeLeft(0);
                    }}
                    disabled={isExecuting || isGeneratingQuote}
                    className="w-full px-4 py-2 bg-[#0D1421] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#2777EF] disabled:opacity-50"
                    required
                  >
                    <option value="">Select sender company</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.companyWallet}>
                        {company.companyName} - {formatCurrency(company.valueAmount)}
                      </option>
                    ))}
                  </select>
                  {getSenderCompany() && (
                    <p className="mt-2 text-sm text-gray-400">
                      Available Balance: {formatCurrency(getSenderCompany()!.valueAmount)}
                    </p>
                  )}
                </div>

                {/* Arrow */}
                <div className="flex justify-center">
                  <div className="bg-[#0D1421] p-3 rounded-full">
                    <ArrowRight className="text-[#2777EF]" size={24} />
                  </div>
                </div>

                {/* Receiver Company */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Receiver Company
                  </label>
                  <select
                    value={receiverCompanyWallet}
                    onChange={(e) => {
                      setReceiverCompanyWallet(e.target.value);
                      setQuote(null); // Reset quote when company changes
                      setTimeLeft(0);
                    }}
                    disabled={isExecuting || isGeneratingQuote}
                    className="w-full px-4 py-2 bg-[#0D1421] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#2777EF] disabled:opacity-50"
                    required
                  >
                    <option value="">Select receiver company</option>
                    {companies
                      .filter(c => c.companyWallet !== senderCompanyWallet)
                      .map((company) => (
                        <option key={company.id} value={company.companyWallet}>
                          {company.companyName} - {formatCurrency(company.valueAmount)}
                        </option>
                      ))}
                  </select>
                  {getReceiverCompany() && (
                    <p className="mt-2 text-sm text-gray-400">
                      Current Balance: {formatCurrency(getReceiverCompany()!.valueAmount)}
                    </p>
                  )}
                </div>

                {/* Get Quote Button */}
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={handleGetQuote}
                    disabled={!senderCompanyWallet || !receiverCompanyWallet || isGeneratingQuote || isExecuting}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isGeneratingQuote ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Generating Quote...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Get Quote
                      </>
                    )}
                  </button>
                </div>

                {/* Quote Display */}
                {quote && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-green-300">Active Quote</h4>
                      <div className="flex items-center text-green-300">
                        <Clock className="h-4 w-4 mr-1" />
                        <span className={`font-mono font-bold ${timeLeft <= 10 ? 'text-red-400 animate-pulse' : ''}`}>
                          {formatTime(timeLeft)}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-400">Exchange Rate</p>
                        <p className="text-white font-semibold">{(quote.exchangeRate / 100).toFixed(2)}%</p>
                      </div>
                      <div>
                        <p className="text-gray-400">Max Amount</p>
                        <p className="text-white font-semibold">{formatCurrency(quote.maxTransactionAmount)}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Amount Input - Only show if quote is available */}
                {quote && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Amount (USD)
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                      <input
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        disabled={isExecuting}
                        placeholder="0.00"
                        step="0.01"
                        min="0.01"
                        max={(quote.maxTransactionAmount / 100).toString()}
                        className="w-full pl-8 pr-4 py-2 bg-[#0D1421] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#2777EF] disabled:opacity-50"
                        required
                      />
                    </div>
                    {amount && (
                      <div className="mt-2 space-y-1">
                        <p className="text-sm text-gray-400">
                          {parseFloat(amount) * 100 > quote.maxTransactionAmount ? (
                            <span className="text-red-400">⚠ Amount exceeds quote maximum</span>
                          ) : parseFloat(amount) * 100 > (getSenderCompany()?.valueAmount || 0) ? (
                            <span className="text-red-400">⚠ Insufficient balance</span>
                          ) : (
                            <span className="text-green-400">✓ Valid amount</span>
                          )}
                        </p>
                        <p className="text-sm text-gray-300">
                          Receiver will get: <span className="font-semibold">
                            {formatCurrency(Math.floor(parseFloat(amount) * 100 * quote.exchangeRate / 10000))}
                          </span>
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isExecuting || isGeneratingQuote}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isExecuting || !quote || !amount || timeLeft === 0}
                  className="px-4 py-2 bg-[#2777EF] text-white rounded-lg hover:bg-[#1F5FD3] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isExecuting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Executing Swap...
                    </>
                  ) : (
                    'Execute Swap'
                  )}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default SwapModal;
